'use client';
import type { ReactNode } from 'react';
import { createI18nClient } from 'next-international/client';
import { ALL_LANGUAGES } from '@/shared/config/brand-languages.mjs';
import { getServerApiBaseUrl } from '@/shared/libs/server-api-base-url';

const translationCache = new Map<string, any>();
const pendingRequests = new Map<string, Promise<any>>();

function getTranslationsBaseUrl(): string {
  if (typeof window !== 'undefined') {
    const clientBaseUrl = process.env.NEXT_PUBLIC_HOSTNAME ?? '/server';
    return clientBaseUrl.replace(/\/$/, '');
  }

  return getServerApiBaseUrl();
}

async function fetchTranslations(locale: string): Promise<any> {
  if (translationCache.has(locale)) {
    return translationCache.get(locale);
  }

  if (pendingRequests.has(locale)) {
    return pendingRequests.get(locale);
  }

  const requestPromise = (async () => {
    try {
      const baseUrl = getTranslationsBaseUrl();
      const url = `${baseUrl}/translations?lang=${encodeURIComponent(locale)}`;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      try {
        const response = await fetch(url, {
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(
            `Failed to fetch locale for ${locale}: ${response.status}`
          );
        }

        const data = await response.json();
        const transformedData = transformLocalizationData(data);
        const result = { default: transformedData };
        translationCache.set(locale, result);

        return result;
      } finally {
        clearTimeout(timeoutId);
      }
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching locale:', error);
      }

      return { default: {} };
    } finally {
      pendingRequests.delete(locale);
    }
  })();

  pendingRequests.set(locale, requestPromise);

  return requestPromise;
}

function transformLocalizationData(
  data: any,
  prefix = '',
  result: Record<string, string> = {}
): Record<string, string> {
  for (let key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      const value = data[key];
      if (value !== null && typeof value === 'object') {
        transformLocalizationData(data[key], `${prefix}${key}.`, result);
      } else {
        result[`${prefix}${key}`] = value;
      }
    }
  }
  return result;
}

export function seedTranslations(
  locale: string,
  data: Record<string, string>
): void {
  if (!translationCache.has(locale) && data && Object.keys(data).length > 0) {
    translationCache.set(locale, { default: data });
  }
}

const locales = ALL_LANGUAGES.reduce(
  (acc, lang) => {
    acc[lang] = () => fetchTranslations(lang);
    return acc;
  },
  {} as Record<string, () => Promise<any>>
);

export type Translate = {
  (key: string, params?: Record<string, string | number | undefined>): string;
  (key: string, params: Record<string, ReactNode>): ReactNode;
};

const i18nClient = createI18nClient(locales);

export const {
  I18nProviderClient,
  useChangeLocale,
  useCurrentLocale,
  ...restI18n
} = i18nClient;

export const useI18n = i18nClient.useI18n as unknown as () => Translate;

export const useScopedI18n = i18nClient.useScopedI18n as unknown as (
  scope: string
) => Translate;
